import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Truck, Clock, MapPin, Package, Phone, Mail } from 'lucide-react';

const ShippingPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-gray-900 to-black py-12 sm:py-16">
          <div className="container mx-auto px-4 text-center">
            <Truck className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Shipping <span className="text-yellow-500">Policy</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
              Fast, secure, and reliable delivery across India
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-8 sm:py-12">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10">
            
            {/* Last Updated */}
            <p className="text-gray-500 text-sm mb-8">Last Updated: January 2025</p>

            {/* Introduction */}
            <section className="mb-8">
              <p className="text-gray-700 leading-relaxed">
                At <strong>Jain Watch Company</strong>, we are committed to delivering your premium timepieces safely and promptly. This Shipping Policy outlines our delivery process, timelines, and charges applicable to orders placed on our website.
              </p>
            </section>

            {/* Shipping Coverage */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <MapPin className="w-6 h-6 text-yellow-500 mr-2" />
                Shipping Coverage
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    We deliver across <strong>all states and union territories of India</strong>.
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    Currently, we do not offer international shipping.
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-500 mr-2">•</span>
                    For remote or restricted areas, delivery may take additional time.
                  </li>
                </ul>
              </div>
            </section>

            {/* Delivery Timeline */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Clock className="w-6 h-6 text-yellow-500 mr-2" />
                Delivery Timeline
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="p-3 text-left text-sm font-semibold">Location</th>
                      <th className="p-3 text-left text-sm font-semibold">Estimated Delivery</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b">
                      <td className="p-3">Metro Cities (Delhi, Mumbai, Bangalore, etc.)</td>
                      <td className="p-3 font-semibold text-green-600">2-4 Business Days</td>
                    </tr>
                    <tr className="border-b bg-gray-50">
                      <td className="p-3">Tier-2 & Tier-3 Cities</td>
                      <td className="p-3 font-semibold text-green-600">4-6 Business Days</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-3">Remote / Rural Areas</td>
                      <td className="p-3 font-semibold text-yellow-600">5-7 Business Days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-gray-600 text-sm mt-4">
                <strong>Note:</strong> Delivery times are estimates and may vary due to unforeseen circumstances such as natural disasters, public holidays, or courier delays.
              </p>
            </section>

            {/* Shipping Charges */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Package className="w-6 h-6 text-yellow-500 mr-2" />
                Shipping Charges
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-bold text-green-700 mb-2">FREE Shipping</h3>
                  <p className="text-gray-700">On all orders above <strong>₹2,000</strong></p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-bold text-yellow-700 mb-2">Standard Shipping</h3>
                  <p className="text-gray-700"><strong>₹99</strong> for orders below ₹2,000</p>
                </div>
              </div>
            </section>

            {/* Order Processing */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Order Processing</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-yellow-500 text-black rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm font-bold">1</span>
                  Orders are processed within <strong>24-48 hours</strong> of payment confirmation.
                </li>
                <li className="flex items-start">
                  <span className="bg-yellow-500 text-black rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm font-bold">2</span>
                  You will receive an order confirmation email with tracking details once your order is dispatched.
                </li>
                <li className="flex items-start">
                  <span className="bg-yellow-500 text-black rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm font-bold">3</span>
                  Orders placed on weekends or public holidays will be processed on the next business day.
                </li>
              </ul>
            </section>

            {/* Tracking */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Order Tracking</h2>
              <p className="text-gray-700 leading-relaxed">
                Once your order is shipped, you will receive an SMS and email with the tracking number and courier partner details. You can track your shipment directly on the courier's website or contact us for assistance.
              </p>
            </section>

            {/* Delivery Issues */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Delivery Issues</h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6">
                <p className="text-gray-700 mb-3">If you face any of the following issues, please contact us immediately:</p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Damaged or tampered packaging upon delivery</li>
                  <li>• Wrong product delivered</li>
                  <li>• Missing items in your order</li>
                  <li>• Delayed shipment beyond the estimated time</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  <strong>Important:</strong> Please do not accept the delivery if the package appears damaged. Refuse the package and inform us immediately.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-gray-900 text-white rounded-lg p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">Need Help?</h2>
              <p className="text-gray-300 mb-4">For any shipping-related queries, reach out to us:</p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-yellow-500 mr-3" />
                  <a href="mailto:jainwatchcompany1975@gmail.com" className="text-yellow-500 hover:underline">
                    jainwatchcompany1975@gmail.com
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-yellow-500 mr-3" />
                  <a href="tel:9927058909" className="text-yellow-500 hover:underline">
                    +91 9927058909
                  </a>
                </div>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShippingPolicy;
